import { useState, useEffect } from 'react';
import { Grid, Switch, Typography, Button, IconTooltip } from '@ts/storybook';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import ReactJson, { InteractionProps } from 'react-json-view';
import { v4 as uuidv4 } from 'uuid';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import SortableItem from './SortableItem';

interface ITextJSONProps {
  label: string;
  value: any;
  onJSONValidated: (json: any) => void;

  disabled?: boolean;
  placeholder?: any;
  isSubtitle?: boolean;
}

//We will create a utils file in storybook, when the component is transferred.
export const ensureIds = (array: any[]) =>
  array.map(item => ({
    ...item,
    id: item.id ?? uuidv4(),
  }));

export const isValidJSON = (str: string | object): boolean => {
  if ((typeof str === 'string' && str.includes('{')) || typeof str === 'object') {
    try {
      JSON.parse(str as string);
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

const TextInputJSONValidator = (props: ITextJSONProps) => {
  const dispatch = useAppDispatch();
  const { label, disabled, value, onJSONValidated, placeholder } = props;
  const [newJSONValue, setNewJSONValue] = useState(value);
  const arrayNewJSONValue = Array.isArray(newJSONValue) ? ensureIds(newJSONValue?.filter(i => i !== null)) : [];
  const [isDragMode, setIsDragMode] = useState(false);

  const onModifyJSON = (change: InteractionProps) => {
    if (!change) return;

    const { new_value, name, namespace, updated_src } = change;
    if (new_value) {
      const intentValue = Object.keys(new_value)[0];
      const parsedValue = typeof intentValue === 'string' && isValidJSON(intentValue) ? JSON.parse(intentValue) : new_value;
      if (namespace.length === 0 && !name) {
        setNewJSONValue(parsedValue);
        onJSONValidated(parsedValue);
        return;
      }
    }
    setNewJSONValue(updated_src);
    onJSONValidated(updated_src);
  };

  const handleDragEnd = (event: any) => {
    if (!isDragMode) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = arrayNewJSONValue.findIndex((item: any) => item.id === active.id);
    const newIndex = arrayNewJSONValue.findIndex((item: any) => item.id === over.id);

    const newItems = arrayMove(arrayNewJSONValue, oldIndex, newIndex);
    setNewJSONValue(newItems);
    onJSONValidated(newItems);
  };

  const getValidJSONSrc = () => {
    if (typeof newJSONValue === 'string') {
      try {
        return JSON.parse(newJSONValue);
      } catch {
        return {};
      }
    }

    if (typeof newJSONValue === 'object' && newJSONValue !== null) {
      return newJSONValue;
    }

    return placeholder ?? {};
  };

  const handleCopy = async () => {
    try {
      const jsonString = JSON.stringify(value, null, 2);
      await navigator.clipboard.writeText(jsonString);
    } catch (err) {
      console.error('err', err);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsedValue = typeof text === 'string' && isValidJSON(text) ? JSON.parse(text) : text;
      setNewJSONValue(parsedValue);
      onJSONValidated(parsedValue);
    } catch (err) {
      console.error('err', err);
    }
  };

  const renderJSONValidator = () => (
    <div className="relative">
      <div className="absolute right-2 top-2 z-1 flex gap-1">
        <Button
          startIcon={
            <IconTooltip
              text="Copy to clipboard"
              className="relative left-1"
              iconClassName="ml-1"
              placement="left"
              color="white"
              size={24}
              name="content_copy"
            />
          }
          onClick={handleCopy}
        />
        {!disabled && (
          <Button
            startIcon={
              <IconTooltip
                text="Paste from clipboard"
                className="relative left-1"
                iconClassName="ml-1"
                placement="left"
                color="white"
                size={24}
                name="content_paste"
              />
            }
            onClick={handlePaste}
          />
        )}
      </div>
      <ReactJson
        src={getValidJSONSrc()}
        theme="apathy"
        collapsed={false}
        enableClipboard={true}
        displayDataTypes={false}
        displayObjectSize={false}
        iconStyle="square"
        onEdit={!disabled && onModifyJSON}
        onAdd={!disabled && onModifyJSON}
        onDelete={!disabled && onModifyJSON}
        style={{ padding: '16px' }}
      />
    </div>
  );

  useEffect(() => {
    if (typeof value === 'string') {
      try {
        setNewJSONValue(JSON.parse(value));
      } catch {
        setNewJSONValue(undefined);
        dispatch(globalActions.notification('Invalid JSON format, please check!'));
      }
    } else {
      setNewJSONValue(value);
    }
  }, [value]);

  return (
    <Grid container direction="column" size={12} spacing={2} className="relative">
      {label && (
        <Typography
          variant={props.isSubtitle ? undefined : 'h6'}
          className={props.isSubtitle ? 'font-medium' : ''}
          text={label}
        />
      )}
      {Array.isArray(newJSONValue) && (
        <Switch checked={isDragMode} onChange={() => setIsDragMode(!isDragMode)} leftLabel="editMode" rightLabel="dragMode" />
      )}
      {isDragMode ? (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={arrayNewJSONValue || []} strategy={verticalListSortingStrategy}>
            {arrayNewJSONValue
              ?.filter(i => i !== null)
              .map((item: any) => {
                const key = item?.id ?? item.name ?? uuidv4();
                return (
                  <SortableItem key={key} id={item?.id ?? item.name}>
                    {!isDragMode ? renderJSONValidator() : <Typography text={item?.name ?? ''} className="text-white" />}
                  </SortableItem>
                );
              })}
          </SortableContext>
        </DndContext>
      ) : (
        renderJSONValidator()
      )}
    </Grid>
  );
};

export default TextInputJSONValidator;
